import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { Textarea } from "../ui/textarea";
import { useCreatePhotography } from "@/api/photography/photography";
import {
  appendNoDuplicateRecordNumberRefine,
  photographySchema,
} from "@/api/photography/photography-validation";

const schema = appendNoDuplicateRecordNumberRefine(
  photographySchema.pick({
    photosDescriptionNumeroEnregistrement: true,
    photosDescriptionTitre: true,
  }),
);

type FormType = z.infer<typeof schema>;

type NewPhotographyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewPhotographyDialog = ({
  onOpenChange,
  open,
}: NewPhotographyDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      photosDescriptionNumeroEnregistrement: "",
      photosDescriptionTitre: "",
    },
  });
  const { mutateAsync } = useCreatePhotography();
  const navigate = useNavigate();

  const onFormSubmit = async (data: FormType) => {
    try {
      const result = await mutateAsync(data);
      navigate(`/edit/photography/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("photosDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createPphotography")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="photosDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>
              {t("photosDescriptionNumeroEnregistrement")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="photosDescriptionTitre"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("photosDescriptionTitre")}</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewPhotographyDialog;
